import {
  call,
  takeLatest,
  put,
  select,
  cancelled,
} from 'redux-saga/effects';
import axios from 'axios';
import http from '@api/http';
import get from 'lodash/get';
import { message } from 'antd';
import getMetricsData from '@api/metricContainer';
import * as types from './types';
import selector from './selectors';
import { intervalSelector, metricSelector, filterSelector } from '../base/selectors';
import { getDiagChartQuery, transferResponse, compareGranularity } from './helper';

function* fetchingToState() {
  const interval = yield select(intervalSelector);
  const metric = yield select(metricSelector);
  const filter = yield select(filterSelector);
  const filterRules = filter.rule;
  if (!metric) {
    return;
  }
  if (!interval.from || !interval.to) {
    yield put({
      type: types.FETCHING_FAILS,
      errorCode: 500,
      errorMsg: 'Invalid interval',
    });
    return;
  }
  const body = getDiagChartQuery(
    filterRules,
    metric,
    interval.from,
    interval.to,
  );
  yield put({
    type: types.SAVE_BIRD_CHART_QUERY,
    body,
  });
  const source = axios.CancelToken.source();
  try {
    const response = yield call(
      getMetricsData,
      body,
      { cancelToken: source.token },
      http,
    );
    const result = transferResponse(metric, { data: response });
    yield put({
      type: types.FETCHING_SUCCESS,
      response: result,
    });
  } catch (error) {
    yield call(message.error, error.message);
    yield put({
      type: types.FETCHING_FAILS,
      errorCode: error.code,
      errorMsg: get(error, 'response.data.message', 'Unknown Error'),
    });
  } finally {
    if (yield cancelled()) {
      source.cancel();
    }
  }
}

function* fetchingSliceToState(action) {
  const intervalStore = yield select(intervalSelector);
  const birdChartStore = yield select(selector);
  const metric = yield select(metricSelector);
  const filter = yield select(filterSelector);
  const filterRules = filter.rule;

  const { from, to } = intervalStore;
  const { sliceFrom, sliceTo } = birdChartStore;

  if (action.type === types.SET_SLICE_INTERVAL && !action.shouldFetch) {
    return;
  }

  const invalidInterval = !from || !to;
  const invalidSliceInterval = !sliceFrom || !sliceTo;
  if (invalidInterval && invalidSliceInterval) {
    yield put({
      type: types.FETCHING_FAILS,
      errorCode: 500,
      errorMsg: 'Invalid interval',
    });
    return;
  }
  if ((sliceFrom === from && sliceTo === to) || invalidSliceInterval) {
    return;
  }
  const compareGranularityProps = {
    sliceFrom,
    sliceTo,
    from,
    to,
  };
  if (compareGranularity(compareGranularityProps)) {
    return;
  }

  const body = getDiagChartQuery(filterRules, metric, sliceFrom, sliceTo);
  yield put({
    type: types.SAVE_BIRD_CHART_QUERY,
    body,
  });
  const source = axios.CancelToken.source();
  try {
    const response = yield call(
      getMetricsData,
      body,
      { cancelToken: source.token },
      http,
    );
    const result = transferResponse(metric, { data: response });
    yield put({
      type: types.FETCHING_SLICE_SUCCESS,
      sliceResponse: result,
    });
  } catch (error) {
    yield call(message.error, error.message);
    yield put({
      type: types.FETCHING_SLICE_FAILS,
      errorCode: error.code,
      errorMsg: get(error, 'response.data.message', 'Unknown Error'),
    });
  } finally {
    if (yield cancelled()) {
      source.cancel();
    }
  }
}

function* setZoomRange(action) {
  const birdChartStore = yield select(selector);
  const data = get(birdChartStore, 'response.data', []);
  const startIdx = Math.ceil((data.length - 1) * action.zoomStart / 100);
  const endIdx = Math.floor((data.length - 1) * action.zoomEnd / 100);
  const [zoomIntervalStart] = data[startIdx];
  const [zoomIntervalEnd] = data[endIdx];
  yield put({
    type: types.SAVE_ZOOM_INTERVAL,
    zoomIntervalStart,
    zoomIntervalEnd,
  });
}

function* watchFetchingToState() {
  yield takeLatest([
    types.FETCHING,
  ], fetchingToState);
}

function* watchFetchingSliceToState() {
  yield takeLatest([
    types.SET_SLICE_INTERVAL,
    types.FETCHING_SLICE,
  ], fetchingSliceToState);
}

function* watchSetZoomRange() {
  yield takeLatest([
    types.SET_ZOOM_RANGE,
  ], setZoomRange);
}

export default [
  watchFetchingToState,
  watchFetchingSliceToState,
  watchSetZoomRange,
];
